import { RohanCache } from "../../service-worker/rohanCache";
import { db, storage } from "../storage";
import logger from "../../utils/logger";
import { CacheKeys, clearCache, readCache } from "../../service-worker/swCache";
import { audioChunkBlobKey, audioChunkDurationKey, audioInsertPrefix } from "../../editor/features/audioInsert/utils";
export class Migration {
  constructor(
    private dexieDB: typeof db,
    private rohanCache: RohanCache,
  ) {
    this.dexieDB = dexieDB;
    this.rohanCache = rohanCache;
  }

  public async migrate() {
    const migrated = await this.dexieDB.kv.get("migrated");
    if (migrated) {
      logger.with({ namespace: "migration" }).info("Migration already completed");
      return;
    }
    const rohanCacheData = await readCache(this.rohanCache);
    await this.migrateNotes(rohanCacheData);
    await this.migrateFolders(rohanCacheData);
    await this.migrateAudio();
    await this.migrateMetadata(rohanCacheData);
    await this.migrateUser(rohanCacheData);
    await this.dexieDB.kv.set("migrated", true);
    await clearCache(this.rohanCache);
    logger.with({ namespace: "migration" }).info("Migration completed");
  }

  private async migrateNotes(rohanCacheData: Awaited<ReturnType<typeof readCache>>) {
    const notes = rohanCacheData[CacheKeys.notes];
    const dirtyNotes = rohanCacheData[CacheKeys.notesDirtySet];
    if (notes && notes.length > 0) {
      await this.dexieDB.notes.upsert(notes, false);
    } else {
      logger.with({ namespace: "migration" }).info("No notes found in rohan cache");
    }
    if (dirtyNotes && dirtyNotes.length > 0) {
      await this.dexieDB.notes.markDirty(
        dirtyNotes.map((id) => id as string),
        true,
      );
    } else {
      logger.with({ namespace: "migration" }).info("No dirty notes found in rohan cache");
    }
  }

  private async migrateFolders(rohanCacheData: Awaited<ReturnType<typeof readCache>>) {
    const folders = rohanCacheData[CacheKeys.folders];
    const dirtyFolders = rohanCacheData[CacheKeys.foldersDirtySet];
    if (folders && folders.length > 0) {
      await this.dexieDB.folders.upsert(folders, false);
    } else {
      logger.with({ namespace: "migration" }).info("No folders found in rohan cache");
    }
    if (dirtyFolders && dirtyFolders.length > 0) {
      await this.dexieDB.folders.markDirty(
        dirtyFolders.map((id) => id as string),
        true,
      );
    } else {
      logger.with({ namespace: "migration" }).info("No dirty folders found in rohan cache");
    }
  }

  private async migrateAudio() {
    const allKeys = await storage.findAllKeysWithPrefix(audioInsertPrefix);
    const audioChunkIds = new Map<string, number[]>();
    for (const key of allKeys) {
      const m = key.toString().match(new RegExp("^" + audioChunkBlobKey("(.+)", "(\\d+)") + "$"));
      if (!m) continue;
      const audioId = m[1];
      const chunkNumber = parseInt(m[2]);
      const chunks = audioChunkIds.get(audioId) ?? [];
      chunks.push(chunkNumber);
      audioChunkIds.set(audioId, chunks);
    }
    for (const [audioId, chunks] of audioChunkIds.entries()) {
      for (const chunk of chunks) {
        const audioBlob = await storage.getItem<Blob>(audioChunkBlobKey(audioId, chunk));
        const duration = await storage.getItem<number>(audioChunkDurationKey(audioId, chunk));
        if (!audioBlob) {
          logger.with({ namespace: "migration" }).info(`No audio blob found for audioId: ${audioId}`);
          continue;
        }
        this.dexieDB.audioChunks.insert({
          id: `${audioId}-${chunk}`,
          audioId: audioId,
          chunkIndex: chunk,
          duration: duration ?? 0,
          blob: audioBlob,
          isDirty: 1,
        });
      }
    }
  }

  private async migrateMetadata(rohanCacheData: Awaited<ReturnType<typeof readCache>>) {
    const metadata = rohanCacheData[CacheKeys.metadata];
    if (metadata && metadata.checkpoint > 0) {
      await this.dexieDB.kv.set("checkpoint", metadata.checkpoint);
    } else {
      logger.with({ namespace: "migration" }).info("No metadata found in rohan cache");
    }
  }

  private async migrateUser(rohanCacheData: Awaited<ReturnType<typeof readCache>>) {
    const user = rohanCacheData[CacheKeys.user];
    if (user && user.userSettings) {
      await this.dexieDB.users.upsert({
        id: "1",
        data: {
          settings: user.userSettings,
        },
        isDirty: user.isDirty ? 1 : 0,
      });
    } else {
      logger.with({ namespace: "migration" }).info("No user found in rohan cache");
    }
  }
}
